import { useCallback, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { Persona } from '@/stores'
import {
  EMPTY_FILTERS,
  applyPersonaFilters,
  computeTagUniverse,
  countActiveFilters,
  parseFiltersFromParams,
  writeFiltersToParams,
  type PersonaFilterState,
  type PersonaScope,
  type PersonaTypeFilter,
} from '@/utils/personaFilters'

interface UsePersonaFiltersOptions {
  currentUserId?: string
  persistToUrl?: boolean
}

function toggle<T>(list: T[], item: T): T[] {
  return list.includes(item) ? list.filter((x) => x !== item) : [...list, item]
}

export function usePersonaFilters(
  personas: Persona[],
  opts: UsePersonaFiltersOptions = {},
) {
  const { currentUserId, persistToUrl = false } = opts

  // Both state sources are always instantiated (rules-of-hooks); only one is
  // used, selected by the constant `persistToUrl` flag.
  const [localState, setLocalState] = useState<PersonaFilterState>(EMPTY_FILTERS)
  const [searchParams, setSearchParams] = useSearchParams()

  const urlState = useMemo(() => parseFiltersFromParams(searchParams), [searchParams])
  const filters = persistToUrl ? urlState : localState

  const setFilters = useCallback(
    (next: PersonaFilterState) => {
      if (persistToUrl) {
        setSearchParams((prev) => writeFiltersToParams(prev, next), { replace: true })
      } else {
        setLocalState(next)
      }
    },
    [persistToUrl, setSearchParams],
  )

  const filtered = useMemo(
    () => applyPersonaFilters(personas, filters, currentUserId),
    [personas, filters, currentUserId],
  )
  const tagUniverse = useMemo(() => computeTagUniverse(personas), [personas])
  const activeCount = countActiveFilters(filters)

  const setScope = useCallback(
    (scope: PersonaScope) => setFilters({ ...filters, scope }),
    [filters, setFilters],
  )
  const toggleTag = useCallback(
    (tag: string) => setFilters({ ...filters, tags: toggle(filters.tags, tag) }),
    [filters, setFilters],
  )
  const toggleType = useCallback(
    (t: PersonaTypeFilter) => setFilters({ ...filters, types: toggle(filters.types, t) }),
    [filters, setFilters],
  )
  const clearAll = useCallback(() => setFilters(EMPTY_FILTERS), [setFilters])

  return { filters, filtered, tagUniverse, activeCount, setScope, toggleTag, toggleType, clearAll }
}
